import React, { useState } from 'react';
import {
  AutoComplete, Input, Icon, Spin,
} from 'antd';
import * as PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import './ViaSearch.css';
import { requestUserSearch } from '../../state/ui/application/viaSearchUIActions';
import { selectUserSearchResult } from '../../state/ui/application/viaSearchUIReducer';

const { Option, OptGroup } = AutoComplete;

const searchWaitMs = 400;
const noActionKey = '*';

/**
 * The summary of a user found by the search in the results drop down.
 * @param {object} props.userDetails The user summary object.
 */
function UserResult({ userDetails }) {
  return (
    <div className="inspection-result">
      <div className="inspection-result--key">
        {userDetails.userId}
      </div>
      <div className="inspection-result--summary">
        <div className="user-result--keywords">
          <div className="inspection-result--keyword">
            <span className="inspection-result--keyword-label">
              <FormattedMessage id="email" />
              :
            </span>
            <span className="inspection-result--keyword-value">{userDetails.username}</span>
          </div>
          <div className="inspection-result--keyword">
            <span className="inspection-result--keyword-label">
              <FormattedMessage id="displayName" />
              :
            </span>
            <span className="inspection-result--keyword-value">{userDetails.displayName}</span>
          </div>
          <div className="inspection-result--keyword">
            <span className="inspection-result--keyword-label">
              <FormattedMessage id="companyName" />
              :
            </span>
            <span className="inspection-result--keyword-value">{userDetails.companyName}</span>
          </div>
          <div className="inspection-result--keyword">
            <span className="inspection-result--keyword-label">
              <FormattedMessage id="addLine1" />
              :
            </span>
            <span className="inspection-result--keyword-value">{userDetails.address ? userDetails.address.slice(0, userDetails.address.indexOf(',')) : <FormattedMessage id="noData" />}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

UserResult.propTypes = {
  userDetails: PropTypes.shape({
    userId: PropTypes.number.isRequired,
    userGroupId: PropTypes.number.isRequired,
    username: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    companyName: PropTypes.string.isRequired,
    address: PropTypes.string,
  }).isRequired,
};


/**
 * Creates all the user option components that are rendered inside of an OptionGroup component.
 * @param {Object[]} userResults An array of user summaries.
 * @param {boolean} userResultsLoading True if the user results are being loaded.
 */
const getUserOptions = (userResults, userResultsLoading) => {
  if (userResultsLoading) return <Option key={noActionKey} disabled><Spin /></Option>;
  if (!userResults || userResults.length <= 0) return <Option key={noActionKey} disabled>No Results</Option>;

  return userResults.map(user => (
    <Option key={user.userId} label={user.userId}>
      <UserResult userDetails={user} />
    </Option>
  ));
};

/**
 * A user search component. This will search the application for active users with usernames containing the search string.
 * @param {object} props All unused props are passed to the child AutoComplete component.
 * @param {object} props.history (HOC) The react-router history object.
 * @param {function} props.doSearch (Redux) The function for dispatching a search action. (string) => void
 */
function ViaUserSearch({
  history, doSearch, userResults, userResultsLoading, onSelect, ...rest
}) {
  const [currentValue, setCurrentValue] = useState();
  const [searchTimeoutId, setSearchTimeoutId] = useState(null);
  const intl = useIntl();

  // When the search string has been changed.
  const onChange = (value) => {
    setCurrentValue(value);
    if (searchTimeoutId) clearTimeout(searchTimeoutId);

    // Wait a short moment after key press before searching.
    const timeoutId = setTimeout(() => {
      doSearch(value);
    }, searchWaitMs);
    setSearchTimeoutId(() => timeoutId);
  };

  // This seems to only work with an option group.
  // If you read this and know how not to use one, do tell me (JMB)
  const userOptions = () => (
    <OptGroup key={intl.formatMessage({ id: 'matchedUser' })}>
      { getUserOptions(userResults, userResultsLoading) }
    </OptGroup>
  );


  // When a search result has been selected.
  const onSelected = (optionKey) => {
    onSelect(optionKey);
    onChange(); // Reset the search state.
  };

  return (
    <AutoComplete
      value={currentValue}
      placeholder={<FormattedMessage id="userSearch" />}
      onChange={onChange}
      onSelect={onSelected}
      optionLabelProp="label"
      dataSource={[userOptions()]}
      {...rest}
    >
      <Input
        suffix={<Icon type="search" style={{ color: 'rgba(0,0,0,.25)' }} />}
        maxLength={20}
        style={{ width: '500px' }}
      />
    </AutoComplete>
  );
}

ViaUserSearch.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  doSearch: PropTypes.func.isRequired,
  userResults: PropTypes.arrayOf(PropTypes.shape({})),
  userResultsLoading: PropTypes.bool.isRequired,
  onSelect: PropTypes.func,
};

ViaUserSearch.defaultProps = {
  userResults: null,
  onSelect: () => {},
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const userSearchResults = selectUserSearchResult(state);

  return {
    userResultsLoading: userSearchResults ? userSearchResults.isLoading : false,
    userResults: userSearchResults ? userSearchResults.results : [],
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  doSearch: searchValue => dispatch(requestUserSearch(searchValue)),
});


export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ViaUserSearch));
